import { create } from 'zustand';
import type { Agent } from '../types/agent';
import type { Message } from '../types/message';

type ConnectionStatus = 'offline' | 'connecting' | 'online';

interface LobsterState {
  // Connection to PC via tunnel
  connection: ConnectionStatus;

  // Agents
  agents: Agent[];
  activeAgentId: string | null;

  // Messages (per-agent by accountId)
  messagesByAgent: Record<string, Message[]>;

  // Actions
  setConnection: (status: ConnectionStatus) => void;
  setAgents: (agents: Agent[]) => void;
  setActiveAgent: (accountId: string | null) => void;
  addMessages: (accountId: string, messages: Message[]) => void;
  clearMessages: (accountId: string) => void;
  updateApproval: (requestId: string, decision: 'approved' | 'rejected') => void;
}

export const useLobsterStore = create<LobsterState>((set, get) => ({
  connection: 'offline',
  agents: [],
  activeAgentId: null,
  messagesByAgent: {},

  setConnection: (connection) => set({ connection }),

  setAgents: (agents) => {
    set({ agents });
    const state = get();
    if (!state.activeAgentId && agents.length > 0) {
      set({ activeAgentId: agents[0].id });
    }
  },

  setActiveAgent: (activeAgentId) => set({ activeAgentId }),

  addMessages: (accountId, messages) =>
    set((state) => {
      const existing = state.messagesByAgent[accountId] || [];
      const existingIds = new Set(existing.map(m => m.messageId));
      const newMsgs = messages.filter(m => !existingIds.has(m.messageId));
      if (newMsgs.length === 0) return state;

      const hasServerUserMsg = newMsgs.some(m => m.from === 'user' && !m.messageId.startsWith('local-'));
      const cleaned = hasServerUserMsg
        ? existing.filter(m => !m.messageId.startsWith('local-'))
        : existing;

      return {
        messagesByAgent: {
          ...state.messagesByAgent,
          [accountId]: [...cleaned, ...newMsgs],
        },
      };
    }),

  clearMessages: (accountId) =>
    set((state) => ({
      messagesByAgent: { ...state.messagesByAgent, [accountId]: [] },
    })),

  updateApproval: (requestId, decision) =>
    set((state) => {
      const updated: Record<string, Message[]> = {};
      for (const [accId, msgs] of Object.entries(state.messagesByAgent)) {
        updated[accId] = msgs.map((m) => {
          if (m.type === 'approval' && m.approval?.requestId === requestId) {
            return { ...m, approval: { ...m.approval, status: decision, respondedAt: Date.now() } };
          }
          return m;
        });
      }
      return { messagesByAgent: updated };
    }),
}));
